import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Mail, MessageSquare, Phone } from "lucide-react";
import { toast } from "sonner";

export default function Notifications() {
    const { activeHotelId } = useAuth();
    const [list, setList] = useState([]);
    const [form, setForm] = useState({ type: "email", recipient: "", subject: "", body: "" });

    const load = async () => {
        if (!activeHotelId) return;
        const { data } = await api.get(`/notifications?hotel_id=${activeHotelId}`);
        setList(data);
    };
    useEffect(() => { load(); }, [activeHotelId]);

    const send = async () => {
        try {
            const { data } = await api.post("/notifications/send", { ...form, hotel_id: activeHotelId });
            toast.success(data.mocked ? "Queued (MOCKED)" : "Delivered");
            setForm({ type: "email", recipient: "", subject: "", body: "" });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Notifications" subtitle="Email · SMS · WhatsApp delivery log">
            <Card className="p-4 mb-4 border border-amber-200 bg-amber-50 rounded-md">
                <div className="text-sm text-amber-900">
                    <strong>MOCKED delivery.</strong> Real email/SMS/WhatsApp delivery requires Resend/SendGrid + Twilio API keys in `/app/backend/.env`. Messages are queued with `mocked=true` and shown in the delivery log below.
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6 border border-slate-200 rounded-md shadow-none">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-4">Send Notification</div>
                    <div className="space-y-3">
                        <div><Label className="text-xs uppercase tracking-wider">Channel</Label>
                            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="email">Email</SelectItem>
                                    <SelectItem value="sms">SMS</SelectItem>
                                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                                </SelectContent>
                            </Select></div>
                        <div><Label className="text-xs uppercase tracking-wider">Recipient</Label>
                            <Input value={form.recipient} onChange={e => setForm({ ...form, recipient: e.target.value })} placeholder={form.type === "email" ? "guest@example.com" : "+91 98765 43210"} data-testid="notif-recipient" /></div>
                        {form.type === "email" && (
                            <div><Label className="text-xs uppercase tracking-wider">Subject</Label>
                                <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="notif-subject" /></div>
                        )}
                        <div><Label className="text-xs uppercase tracking-wider">Message</Label>
                            <Textarea rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} data-testid="notif-body" /></div>
                        <Button onClick={send} disabled={!form.recipient || !form.body} className="w-full bg-[#0A1C3E] rounded-sm" data-testid="notif-send">
                            <Send className="w-4 h-4 mr-1" /> Send
                        </Button>
                    </div>
                </Card>

                <Card className="p-6 border border-slate-200 rounded-md shadow-none">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-4">Delivery Log</div>
                    <div className="space-y-2 max-h-[500px] overflow-auto">
                        {list.map(n => {
                            const Icon = n.type === "email" ? Mail : n.type === "sms" ? Phone : MessageSquare;
                            return (
                                <div key={n.id} className="p-3 border border-slate-200 rounded" data-testid={`notif-${n.id}`}>
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-2">
                                            <Icon className="w-3.5 h-3.5 text-slate-500" />
                                            <span className="text-xs uppercase tracking-wider text-slate-500">{n.type}</span>
                                        </div>
                                        <span className={`status-badge ${n.mocked ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-emerald-100 text-emerald-800 border-emerald-200"}`}>
                                            {n.status}
                                        </span>
                                    </div>
                                    <div className="text-sm mt-1">To: <span className="font-mono">{n.recipient}</span></div>
                                    {n.subject && <div className="text-sm font-semibold mt-1">{n.subject}</div>}
                                    <div className="text-xs text-slate-600 mt-1 line-clamp-2">{n.body}</div>
                                    <div className="text-[10px] text-slate-400 mt-1">{n.created_at?.slice(0, 19).replace("T", " ")}</div>
                                </div>
                            );
                        })}
                        {list.length === 0 && <div className="text-center text-slate-500 text-sm py-8">No notifications yet</div>}
                    </div>
                </Card>
            </div>
        </AppShell>
    );
}
